import { useEffect, useRef, useState } from "react";
import {
  crossIcon,
  rightDirectionBlackIcon,
  searchIcon,
} from "../../../assets";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import Drawer from "../../../component-lib/Organisms/drawer/EventEHRDrawer";
import useDeviceType from "../../../custom-hooks/DeviceType";
import Button from "../../../component-lib/Molecules/button/Button";
import EventEHRCreateVisitDialog from "../../../component-lib/Organisms/create-visit-dialog/EventEHRCreateVisitDialog";
import useHasOverflow from "../../../custom-hooks/HasOverflow";
import PatientProfileNotFound from "./PatientProfileNotFound";
import CreatePatientForm from "./CreatePatientForm";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import SearchSelectDropdown from "../../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import { useDispatch, useSelector } from "react-redux";
import {
  convertParticipantToPatient,
  createNewPatient,
  searchParticipants,
} from "../patientSaga";
import { componentKey, getSearchedPatientList } from "../patientSlice";
import ViewPatientProfile from "./ViewPatientProfile";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import ViewPatientProfileAccordion from "./ViewPatientProfileAccordion";
type CreatePatientDrawerPropsType = {
  open: boolean;
  closeCb: () => void;
};

const CreatePatientDrawer = ({
  open,
  closeCb,
}: CreatePatientDrawerPropsType) => {
  const { searchedPatientList, searchedPatientTotalPages } = useSelector(
    (state: any) => state[componentKey],
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const divRef = useRef<HTMLDivElement>(null);
  const hasOverflow = useHasOverflow(divRef);
  const deviceType = useDeviceType();
  const [openCreateVisitDialog, setOpenCreateVisitDialog] = useState(false);
  const [isAddNewPatientDialogOpen, setIsAddNewPatientDialogOpen] =
    useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [error, setError] = useState("");
  const [searchType, setSearchType] = useState<"name" | "bsa">("name");
  const [noParticipantFoundData, setNoParticipantFoundData] = useState({
    type: "",
    query: "",
  });

  // Infinite scroll for the participant search results.
  const SEARCH_PAGE_LIMIT = 25;
  const [pageNumber, setPageNumber] = useState(1);
  const isLoadingMoreRef = useRef(false);
  // There are more pages while the current page is below the API's totalPages.
  const hasMore = pageNumber < (searchedPatientTotalPages ?? 1);

  // A fresh result set has arrived — allow the next scroll-load.
  useEffect(() => {
    isLoadingMoreRef.current = false;
  }, [searchedPatientList]);

  // Load the next page when the results list is scrolled near the bottom.
  const handleResultsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (
      nearBottom &&
      hasMore &&
      !isLoadingMoreRef.current &&
      searchQuery?.trim().length >= 3
    ) {
      isLoadingMoreRef.current = true;
      const next = pageNumber + 1;
      setPageNumber(next);
      dispatch(
        searchParticipants({
          searchKey: searchQuery,
          pageNumber: next,
          limit: SEARCH_PAGE_LIMIT,
        }),
      );
    }
  };

  // Handle Search field value change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value?.trim());

    // Check the length and set error
    if (value?.length > 0 && value?.length < 3) {
      setError("Please enter at least 3 characters to perform the search");
    } else {
      setError("");
    }

    if (!value) {
      // try {
      //   dispatch(searchPatient({ searchKey: value }));
      // } catch (e) {
      //   console.log("error:", e);
      // }
      dispatch(getSearchedPatientList(null));
    }
    // Detect numeric input for BSA ID
    if (/^\d+$/.test(value?.trim())) {
      setSearchType("bsa");
    } else if (/^[A-Za-z\s]+$/.test(value?.trim())) {
      setSearchType("name");
    }
    // Determine type: Assume names contain letters and BSA IDs are alphanumeric
    // if (/^\d+$/.test(value)) {
    //   setSearchType("bsa");
    // } else {
    //   setSearchType("name");
    // }
  };

  // HANDLE SEARCH PATIENT
  const handleSearch = () => {
    try {
      setPageNumber(1);
      isLoadingMoreRef.current = false;
      dispatch(
        searchParticipants({
          searchKey: searchQuery,
          pageNumber: 1,
          limit: SEARCH_PAGE_LIMIT,
        }),
      );
      setNoParticipantFoundData({ query: searchQuery, type: searchType });
    } catch (e) {
      console.log("error:", e);
    }
  };

  const handleNewPatientData = (values: any) => {
    const payload = {
      apiPayload: {
        firstName: values?.firstName,
        lastName: values?.lastName,
        bsaId: values?.bsaId,
        email: values?.email,
        dateOfBirth: moment(values?.dateOfBirth)?.format("YYYY-MM-DD"),
        contactNumber: values?.contact,
        gender: values?.gender,
        campId: values?.clinicLocation,
        emergencyContact: {
          contactName: values?.emergencyContactName,
          contactRelation: values?.emergencyContactRelation,
          contactEmail: values?.emergencyEmail,
          contactNumber: values?.emergencyContactNumber,
        },
        status: "Active",
      },
      navigate,
      comesFrom: "addPatient",
    };
    dispatch(createNewPatient(payload));
  };

  // HANDLE CONVERT PARTICIPANT TO PATIENT function call
  const handleConvertParticipantToPatient = (data: any) => {
    const participantToPatientConversionData = {
      navigate,
      payload: {
        participantId: data?.id,
        key: "check",
        firstName: data?.firstName,
        lastName: data?.lastName,
        bsaId: data?.bsaId,
        campId: data?.baseCamp?.id,
      },
      comesFrom: "createVisitFromSearchParticipantCard",
    };
    dispatch(convertParticipantToPatient(participantToPatientConversionData));
  };

  return (
    <Drawer open={open} onCloseCb={closeCb} style={{ minWidth: "350px" }}>
      {/* Full-height flex column: the header keeps its natural height and the
          scroll panes below fill the rest. Avoids 100vh, which on iOS Safari
          is taller than the visible viewport and pushed the submit button
          off-screen. */}
      <div
        className={`flex flex-col h-full overflow-hidden ${deviceType === "mobile" ? "w-[350px]" : "w-[708px]"}`}
      >
        {/* DIALOG HEADING */}
        <div className="flex justify-between py-5 border border-neutral-20 px-6 items-center">
          <div className="flex items-center gap-4">
            {/* IF ADD NEW PATIENT FLOW IS OPEN THEN WE ARE SHOWING BACK ICON */}
            {isAddNewPatientDialogOpen && (
              <img
                src={rightDirectionBlackIcon}
                alt="back-icon"
                className="cursor-pointer"
                onClick={() => {
                  setSearchQuery("");
                  setIsAddNewPatientDialogOpen(false);
                }}
              />
            )}
            <Heading
              title={
                isAddNewPatientDialogOpen ? "Add New Patient" : "Patient Search"
              }
              customStyle={{ fontWeight: "600" }}
            />
          </div>
          <img
            src={crossIcon}
            alt={"search"}
            className="cursor-pointer w-[15.34px] h-[15.34px]"
            onClick={() => {
              closeCb();
              dispatch(getSearchedPatientList(null));
            }}
          />
        </div>
        {/* SEARCH INPUT AND BUTTON */}
        {!isAddNewPatientDialogOpen && (
          <div
            className="flex py-6 px-6 gap-6"
            onKeyDown={(e) => {
              // Allow searching via the Enter key (min 3 characters).
              if (e.key === "Enter" && searchQuery?.trim().length >= 3) {
                e.preventDefault();
                handleSearch();
              }
            }}
          >
            <SearchSelectDropdown
              freeSolo={true}
              searchedValue={searchQuery}
              onSearchValueChangeCb={(e) => handleChange(e)}
              size="small"
              label="Search by BSA ID or Last Name"
              fullWidth="100%"
              startIcon={
                <img
                  alt="search-icon"
                  src={searchIcon}
                  width={"18px"}
                  height={"18px"}
                />
              }
            />

            <Button
              // disabled={searchQuery.trim()?.length === 0}
              disabled={searchQuery?.trim().length < 3}
              label="Search"
              variant="contained"
              onClickCb={() => {
                // closeCb();
                handleSearch();
              }}
              style={{ padding: "6px 20px" }}
            ></Button>
          </div>
        )}
        {/* Error Message */}
        <div className="ml-6">
          {error && <p className="text-red-500 text-sm">{error}</p>}
        </div>

        {/* MAIN CONTENT */}
        {/* This will be conditionally handle later on */}
        {!isAddNewPatientDialogOpen && (
          <div
            ref={divRef}
            onScroll={handleResultsScroll}
            // Fills the remaining drawer height (min-h-0 lets the flex child
            // shrink so it scrolls instead of overflowing).
            className={`flex-1 min-h-0 mx-6 overflow-auto ${hasOverflow ? "pr-3" : "pr-1"} pl-1`}
          >
            {/* IF RESULT FOUND BY SEARCH BY PATIENT ID */}
            {searchType === "bsa" &&
              searchedPatientList?.length > 0 &&
              searchQuery?.length >= 3 && (
                <div className="flex flex-col gap-4">
                  {searchedPatientList?.map((data: any) => (
                    <ViewPatientProfile
                      key={data?.id}
                      patientProfileData={data}
                      getProfileButtonClickEvent={(data) => {
                        dispatch(getSearchedPatientList(null));
                        navigate(`/patient/${data?.patient?.[0]?.id}`);
                      }}
                      getCreateVisitButtonClickEvent={(data) => {
                        handleConvertParticipantToPatient(data);
                      }}
                    />
                  ))}
                </div>
              )}

            {/* IF RESULT NOT FOUND BY SEARCH BY PATIENT ID */}
            {!isAddNewPatientDialogOpen &&
              searchedPatientList?.length === 0 &&
              searchQuery?.length >= 3 && (
                <PatientProfileNotFound
                  searchedType={noParticipantFoundData.type}
                  searchedValue={noParticipantFoundData.query}
                  getAddNewPatientButtonClickEvent={() => {
                    setIsAddNewPatientDialogOpen(true);
                  }}
                />
              )}

            {/* IF USER SEARCH PATIENT BY NAME THEN THIS TABLE WILL OPEN*/}
            {/* <SearchedPatientList /> */}
            {searchType === "name" &&
              searchedPatientList?.length > 0 &&
              searchQuery?.length >= 3 && (
                <div>
                  {searchedPatientList?.map((data: any) => (
                    <ViewPatientProfileAccordion
                      key={data?.bsaId}
                      data={data}
                      handleViewProfileClick={(data) => {
                        dispatch(getSearchedPatientList(null));
                        navigate(`/patient/${data?.patient?.[0]?.id}`);
                      }}
                      handleVisitButtonClick={(data) => {
                        handleConvertParticipantToPatient(data);
                      }}
                    />
                  ))}
                </div>
              )}
          </div>
        )}

        {/* IF USER CLICK ADD NEW PATIENT BUTTON THEN THIS SCREEN WILL OPEN.
            The container itself is mounted conditionally (not just its
            contents) so only ONE flex-1 pane exists at a time — two would
            split the drawer height 50/50 and leave the search results
            half-height. It also keeps `divRef` bound to the visible pane. */}
        {isAddNewPatientDialogOpen && (
          <div
            ref={divRef}
            className={`flex-1 min-h-0 mx-6 overflow-auto ${hasOverflow ? "pr-3" : "pr-1"}`}
          >
            <CreatePatientForm
              handlePatientFieldsData={(values) => {
                handleNewPatientData(values);
              }}
            />
          </div>
        )}
      </div>
      <EventEHRCreateVisitDialog
        dialogHeading={TEXT_CONSTANTS?.CREATE_VISIT}
        isOpen={openCreateVisitDialog}
        handleClose={() => {
          setOpenCreateVisitDialog(false);
        }}
        onEMSVisitSelected={() => navigate("/patient/jot-form")}
      />
    </Drawer>
  );
};

export default CreatePatientDrawer;
